import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, MessageCircle, Settings, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/cursos", label: "Cursos & Aulas", icon: BookOpen, end: false },
    { to: "/admin/usuarios", label: "Usuárias", icon: Users, end: false },
    { to: "/admin/depoimentos", label: "Depoimentos", icon: MessageCircle, end: false },
    { to: "/admin/configuracoes", label: "Configurações", icon: Settings, end: false },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <aside className="hidden lg:flex w-72 flex-col" style={{ borderRight: "1px solid var(--border-light)", background: "var(--surface)" }}>
        <div className="p-9" style={{ borderBottom: "1px solid var(--border-light)" }}>
          <Link to="/" className="brand-logo text-xl">
            <span className="ana">Ana Paula</span> <span className="m">Moraes</span>
          </Link>
          <p className="text-[10px] mt-2 uppercase tracking-[0.32em] font-semibold" style={{ color: "var(--copper)" }}>Painel Admin</p>
        </div>

        <nav className="flex-1 p-5 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive ? "shadow-md" : "hover:bg-[color:var(--bg-warm)]"
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? "white" : "var(--text-primary)",
                background: isActive ? "var(--rose-gold)" : "transparent",
              })}
              data-testid={`admin-nav-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <it.icon size={17} strokeWidth={1.7} />
              {it.label}
            </NavLink>
          ))}
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium hover:bg-[color:var(--bg-warm)] transition-all" style={{ color: "var(--text-primary)" }} data-testid="admin-view-site">
            <ExternalLink size={17} strokeWidth={1.7} /> Ver Site
          </a>
        </nav>

        <div className="p-6" style={{ borderTop: "1px solid var(--border-light)" }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-base" style={{ background: "var(--rose-soft)", color: "var(--text-primary)" }}>
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{user?.name}</div>
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm hover:text-[color:var(--copper)] transition-colors" style={{ color: "var(--text-secondary)" }} data-testid="admin-logout">
            <LogOut size={15} /> Sair
          </button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 px-5 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--border-light)" }}>
        <Link to="/admin" className="brand-logo text-lg">
          <span className="ana">Admin</span> <span className="m">Moraes</span>
        </Link>
        <button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="admin-mobile-menu" style={{ color: "var(--text-primary)" }}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed top-16 left-0 right-0 z-40 p-5 space-y-1 shadow-xl" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border-light)" }}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isActive ? "text-white" : ""}`}
              style={({ isActive }) => ({
                background: isActive ? "var(--rose-gold)" : "transparent",
                color: isActive ? "white" : "var(--text-primary)",
              })}
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-3 text-sm w-full" style={{ color: "var(--text-secondary)" }}>
            <LogOut size={15} /> Sair
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-0 mt-16 lg:mt-0 overflow-x-hidden min-w-0">
        {children}
      </main>
    </div>
  );
}
